import clsx from "clsx"
import React, { type InputHTMLAttributes, useState, memo, useCallback } from "react"
import { Link } from "react-router-dom"
import { twJoin, twMerge } from "tailwind-merge"
import { ReactComponent as EyeCutIcon } from "@assets/icons/eye_cut.svg"
import { ReactComponent as EyeIcon } from "@assets/icons/eye.svg"

interface PennyInputProps extends InputHTMLAttributes<HTMLInputElement> {
  className?: string
  typography?: "large_title" | "body_regular"
  error?: boolean | number | string
  tags?: string
  label?: string
  icon?: React.ReactElement | string
  message?: string
  messageicon?: React.ReactElement
  labelHighlighted?: boolean
  linkLabel?: string
  linkLabelTo?: string
  withToggleShowPassword?: boolean
}

type RefType = HTMLInputElement

const PennyInput = React.forwardRef<RefType, PennyInputProps>((props, ref) => {
  const {
    className,
    typography = "body_regular",
    error,
    readOnly,
    label,
    disabled,
    tags,
    icon,
    message,
    messageicon,
    labelHighlighted,
    linkLabel,
    linkLabelTo,
    withToggleShowPassword,
  } = props

  const variation_typography = {
    large_title: "text-pn-large-title",
    body_regular: "text-pn-body-regular",
  }

  const [showPassword, setShowPassword] = useState(false)

  const toggleShowPassword = useCallback(() => {
    setShowPassword((prev) => !prev)
  }, [])

  return (
    <div className={clsx(!!label && "flex flex-col gap-y-2")}>
      <div className={twJoin(tags ? "flex items-center gap-x-1.5" : "block")}>
        {!!label && (
          <label
            className={twJoin(
              "text-pn-callout-emphasis text-pn_gray-950",
              disabled && "text-pn_gray-300",
              labelHighlighted && "bg-[#F4E34E] w-fit",
              linkLabel && linkLabelTo && "flex items-center",
            )}
          >
            {label}
            {linkLabel && linkLabelTo && (
              <Link to={linkLabelTo} className="text-pn-callout-link text-pn_cyan-500 !font-semibold ml-auto">
                {linkLabel}
              </Link>
            )}
          </label>
        )}
        {!!tags && (
          <div className="text-[11px] font-semibold uppercase text-pn_gray-950 py-[2.5px] px-2 bg-pn_gray-300 rounded-[4px]">
            {tags}
          </div>
        )}
      </div>

      <div className="relative">
        {icon && <div className="absolute top-1/2 transform -translate-y-1/2 left-4">{icon}</div>}
        <input
          {...props}
          ref={ref}
          type={showPassword ? "text" : props.type}
          className={twMerge(
            "bg-pn_white border-2 w-full border-pn_gray-200 rounded-lg py-[15px] px-4 outline-none text-pn_gray-950 placeholder:text-pn_gray-300 hover:border-pn_gray-950 focus:border-pn_cyan-500 focus:shadow-[0_0_0_4px_#DAF4FF] disabled:bg-pn_paper-2 disabled:border-pn_gray-200",
            className,
            variation_typography[typography as keyof typeof variation_typography],
            !!error && "border-pn_red-500 bg-pn_red-50",
            readOnly && "border-none bg-transparent py-0 px-0 hover:border-none focus:shadow-none rounded-none",
            icon && "pl-10",
            withToggleShowPassword && "pr-12",
          )}
        />
        {withToggleShowPassword && (
          <button
            onClick={toggleShowPassword}
            type="button"
            className="absolute top-1/2 transform -translate-y-1/2 right-4"
          >
            {showPassword ? <EyeIcon /> : <EyeCutIcon />}
          </button>
        )}
      </div>
      {(message || messageicon) && (
        <div className={twMerge(messageicon ? "flex items-center gap-x-[5px]" : "block")}>
          {messageicon && messageicon}
          {!!message && <span className="text-pn-caption-regular text-pn_gray-600">{message}</span>}
        </div>
      )}
    </div>
  )
})

export default memo(PennyInput)
